import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { TableSkeleton } from "@/components/shared/loading-skeleton";
import { postApiExecutions } from "@/lib/api/generated/workflows/workflows";
import { formatTimestamp, truncateId } from "@/lib/utils/formatting";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useAuth } from "@/features/auth/auth-provider";
import {
  type ExecutionStatusTab,
  useExecutionsUiStore,
} from "@/stores/executions-ui-store";

const STATUS_TABS: { value: ExecutionStatusTab; label: string }[] = [
  { value: "all", label: "All" },
  { value: "running", label: "Running" },
  { value: "succeeded", label: "Succeeded" },
  { value: "failed", label: "Failed" },
];

function parseStatusParam(param: string | null): ExecutionStatusTab {
  if (param && STATUS_TABS.some((t) => t.value === param)) {return param as ExecutionStatusTab;}
  return "all";
}

export function ExecutionListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenant } = useAuth();
  const activeTenantId = tenant?.tenantId;
  const { data: executions, isLoading } = useQuery({
    queryKey: ["executions", activeTenantId],
    queryFn: () => postApiExecutions({ tenantId: activeTenantId }),
    enabled: Boolean(activeTenantId),
  });
  useDocumentTitle("Executions");
  const statusFilter = useExecutionsUiStore((state) => state.statusFilter);
  const setStatusFilter = useExecutionsUiStore((state) => state.setStatusFilter);
  const workflowFilter = useExecutionsUiStore((state) => state.workflowFilter);
  const setWorkflowFilter = useExecutionsUiStore((state) => state.setWorkflowFilter);

  useEffect(() => {
    setStatusFilter(parseStatusParam(searchParams.get("status")));
  }, [searchParams, setStatusFilter]);

  const filtered = executions
    ? executions
        .filter((e) => statusFilter === "all" || e.status === statusFilter)
        .filter((e) => !workflowFilter || e.workflowKey.toLowerCase().includes(workflowFilter.toLowerCase()))
    : [];

  const sorted = [...filtered].toSorted((a, b) => b.startedAt.localeCompare(a.startedAt));

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold" style={{ color: "var(--text-strong)" }}>Executions</h1>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className="px-3 py-1.5 text-xs font-medium rounded-[var(--radius-sm)] transition-colors"
              style={{
                background: statusFilter === tab.value ? "var(--accent)" : "transparent",
                color: statusFilter === tab.value ? "var(--text-strong)" : "var(--muted-foreground)",
              }}
              onClick={() => setStatusFilter(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <input
          type="text"
          placeholder="Filter by workflow..."
          value={workflowFilter}
          onChange={(e) => setWorkflowFilter(e.target.value)}
          className="h-8 px-3 text-xs rounded-[var(--radius-sm)] border border-border bg-transparent outline-none focus:border-ring"
          style={{ color: "var(--text)", minWidth: 160 }}
        />
      </div>

      {isLoading ? (
        <TableSkeleton rows={5} cols={5} />
      ) : sorted.length === 0 ? (
        <EmptyState title="No executions" description="Workflow executions will appear here." />
      ) : (
        <div className="border border-border rounded-[var(--radius-md)] overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: "var(--bg-accent)" }}>
                <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Execution</th>
                <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Workflow</th>
                <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Status</th>
                <th className="text-right px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Started</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((exec) => (
                <tr
                  key={exec.executionId}
                  className="border-t border-border cursor-pointer hover:bg-[var(--bg-hover)] transition-colors"
                  onClick={() => navigate(`/executions/${exec.executionId}`)}
                >
                  <td className="px-4 py-2.5 font-mono" style={{ color: "var(--text-strong)" }}>
                    {truncateId(exec.executionId)}
                  </td>
                  <td className="px-4 py-2.5">{exec.workflowKey}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={exec.status} /></td>
                  <td className="px-4 py-2.5 text-right" style={{ color: "var(--muted-foreground)" }}>
                    {formatTimestamp(exec.startedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
